import apiClient from "./axiosConfig";
import type { 
  DashboardStatsResponse, 
  SignupChartResponse,
  FeatureUsageResponse,
  DAUBreakdownResponse,
  PeakUsageHoursResponse,
  TopUsersResponse,
  RecentActivityResponse,
  MealListResponse, 
  UserListResponse,
  UsersDashboardResponse,
  AdminUsersListResponse,
  UserDetailResponse,
  MealsDashboardResponse,
  BiteBalanceTrackingResponse,
  FitnessResponse,
} from "./types";

// Admin Dashboard APIs
export const dashboardApi = {
  // Fetch dashboard stats
  getStats: async (date?: string): Promise<DashboardStatsResponse> => {
    const response = await apiClient.get<DashboardStatsResponse>(
      "/food_scanner/admin/dashboard/stats",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch signups chart data
  getSignupsChart: async (date?: string): Promise<SignupChartResponse> => {
    const response = await apiClient.get<SignupChartResponse>(
      "/food_scanner/admin/dashboard/signups-chart",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch feature usage data
  getFeatureUsage: async (date?: string): Promise<FeatureUsageResponse> => {
    const response = await apiClient.get<FeatureUsageResponse>(
      "/food_scanner/admin/dashboard/feature-usage",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch DAU breakdown data
  getDAUBreakdown: async (date?: string): Promise<DAUBreakdownResponse> => {
    const response = await apiClient.get<DAUBreakdownResponse>(
      "/food_scanner/admin/dashboard/dau-breakdown",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch peak usage hours data
  getPeakUsageHours: async (date?: string): Promise<PeakUsageHoursResponse> => {
    const response = await apiClient.get<PeakUsageHoursResponse>(
      "/food_scanner/admin/dashboard/peak-usage-hours",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch top users data
  getTopUsers: async (date?: string): Promise<TopUsersResponse> => {
    const response = await apiClient.get<TopUsersResponse>(
      "/food_scanner/admin/dashboard/top-users",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch recent activity data
  getRecentActivity: async (date?: string): Promise<RecentActivityResponse> => {
    const response = await apiClient.get<RecentActivityResponse>(
      "/food_scanner/admin/dashboard/recent-activity",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch users dashboard data
  getUsersDashboard: async (date?: string): Promise<UsersDashboardResponse> => {
    const response = await apiClient.get<UsersDashboardResponse>(
      "/food_scanner/admin/dashboard/users/dashboard",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch users list
  // TODO: Add status and search parameters when backend filtering is ready
  // Example: getUsersList: async (date?: string, status?: string, search?: string)
  // API call: { params: { date, status, search } }
  getUsersList: async (date?: string): Promise<AdminUsersListResponse> => {
    const response = await apiClient.get<AdminUsersListResponse>(
      "/food_scanner/admin/dashboard/users/list",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch meals dashboard data
  getMealsDashboard: async (date?: string): Promise<MealsDashboardResponse> => {
    const response = await apiClient.get<MealsDashboardResponse>(
      "/food_scanner/admin/dashboard/meals-glucose/dashboard",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch bite balance tracking data
  getBiteBalanceTracking: async (date?: string): Promise<BiteBalanceTrackingResponse> => {
    const response = await apiClient.get<BiteBalanceTrackingResponse>(
      "/food_scanner/admin/dashboard/bite-balance/tracking",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },

  // Fetch fitness dashboard data
  getFitnessData: async (date?: string): Promise<FitnessResponse> => {
    const response = await apiClient.get<FitnessResponse>(
      "/food_scanner/admin/dashboard/fitness",
      { params: date ? { date } : undefined }
    );
    return response.data;
  },
};

// Meal APIs
export const mealApi = {
  // Fetch all meals
  getAll: async (params?: {
    date?: string;
    userId?: string;
    type?: string;
  }): Promise<MealListResponse> => {
    const response = await apiClient.get<MealListResponse>(
      "/food_scanner/meals",
      { params }
    );
    return response.data;
  },

  // Get meal by ID
  getById: async (id: string) => {
    const response = await apiClient.get(`/food_scanner/meals/${id}`);
    return response.data;
  },

  // Create new meal
  create: async (data: any) => {
    const response = await apiClient.post("/food_scanner/meals", data);
    return response.data;
  },

  // Update meal
  update: async (id: string, data: any) => {
    const response = await apiClient.put(`/food_scanner/meals/${id}`, data);
    return response.data;
  },

  // Delete meal
  delete: async (id: string) => {
    const response = await apiClient.delete(`/food_scanner/meals/${id}`);
    return response.data;
  },
};

// User APIs
export const userApi = {
  // Fetch all users
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    date?: string;
  }): Promise<UserListResponse> => {
    const response = await apiClient.get<UserListResponse>(
      "/food_scanner/admin/users",
      { params }
    );
    return response.data;
  },

  // Get user by ID
  getById: async (id: string) => {
    const response = await apiClient.get(`/food_scanner/admin/users/${id}`);
    return response.data;
  },

  // Get user detail by ID (for admin dashboard)
  getUserDetail: async (id: string, date?: string): Promise<UserDetailResponse> => {
    const response = await apiClient.get<UserDetailResponse>(
      `/food_scanner/admin/dashboard/users/${id}/detail`,
      { params: date ? { date } : undefined }
    );
    return response.data;
  },
};
